import { get, post, put, del } from './client.js';
import type {
  Customer,
  CustomerEmail,
  CustomerCategory,
  CustomerContact,
  PaginatedResponse,
  CreateCustomerRequest,
  EnrichmentStatus,
} from '../types/index.js';

export const customerApi = {
  list: (params?: {
    page?: number;
    per_page?: number;
    company_name?: string;
    email?: string;
    keyword?: string;
    category_id?: number;
    nation_id?: number;
    has_email?: number;
    created_start?: number;
    created_end?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    if (params?.keyword) query.append('keyword', params.keyword);
    if (params?.company_name) query.append('company_name', params.company_name);
    if (params?.email) query.append('email', params.email);
    if (params?.category_id != null) query.append('category_id', params.category_id.toString());
    if (params?.nation_id != null) query.append('nation_id', params.nation_id.toString());
    if (params?.has_email != null) query.append('has_email', params.has_email.toString());
    if (params?.created_start != null) query.append('created_start', params.created_start.toString());
    if (params?.created_end != null) query.append('created_end', params.created_end.toString());
    return get<PaginatedResponse<Customer>>(`/customers?${query.toString()}`);
  },

  get: (id: number) => get<Customer>(`/customers/${id}`),

  create: (data: CreateCustomerRequest) =>
    post<Customer>('/customers', data),

  update: (id: number, data: Partial<CreateCustomerRequest>) =>
    put<Customer>(`/customers/${id}`, data),

  delete: (id: number) =>
    del<{ message: string }>(`/customers/${id}`),

  emails: (id: number, params?: { page?: number; per_page?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.per_page) query.append('per_page', params.per_page.toString());
    const qs = query.toString();
    return get<PaginatedResponse<CustomerEmail>>(`/customers/${id}/emails${qs ? '?' + qs : ''}`);
  },

  matchByEmails: (emails: string[]) =>
    post<Array<{ email: string; customer: Customer }>>('/customers/match-by-emails', { emails }),

  simpleList: () =>
    get<Customer[]>('/customers/list/simple'),

  addEmail: (customerId: number, email: string, isPrimary: number = 0) =>
    post<{ id: number; email: string }>(`/customers/${customerId}/emails`, { email, is_primary: isPrimary }),

  updateEmail: (customerId: number, emailId: number, email: string) =>
    put<{ message: string }>(`/customers/${customerId}/emails/${emailId}`, { email }),

  deleteEmail: (customerId: number, emailId: number) =>
    del<{ message: string }>(`/customers/${customerId}/emails/${emailId}`),

  setPrimaryEmail: (customerId: number, emailId: number) =>
    put<{ message: string }>(`/customers/${customerId}/emails/${emailId}`, { is_primary: 1 }),

  timeline: (customerId: number, perPage: number = 10) =>
    get<{ data: Array<{ id: number; subject: string; email_date: number; send_from: any; folder: string }> }>(
      `/customers/${customerId}/timeline?per_page=${perPage}`
    ),

  searchRecipients: (q: string) =>
    get<Array<{ email: string; name: string }>>(`/customers/recipients/search?q=${encodeURIComponent(q)}`),

  contacts: (customerId: number) =>
    get<CustomerContact[]>(`/customers/${customerId}/contacts`),

  startEnrichment: (layers: string[], customerIds?: number[]) =>
    post<{ started: string[]; message: string }>('/customers/enrich/start', { layers, customer_ids: customerIds }),

  enrichmentStatus: () =>
    get<EnrichmentStatus>('/customers/enrich/status'),
};

export const categoryApi = {
  list: () => get<CustomerCategory[]>('/customers/categories'),
  create: (name: string) => post<CustomerCategory>('/customers/categories', { name }),
  update: (id: number, name: string) =>
    put<CustomerCategory>(`/customers/categories/${id}`, { name }),
  delete: (id: number) => del<{ message: string }>(`/customers/categories/${id}`),
};
